import { Alert, Box, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FormDialog } from './Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { MyTenant } from '../../core/types/data-models';

interface EditTenantDialogProps {
  open: boolean;
  onClose: () => void;
  /** The caller's active tenant, as loaded by the page — null while it's still loading. */
  tenant: MyTenant | null;
  loading?: boolean;
  /** Called after a successful rename so the caller can refresh its tenant query. */
  onUpdated: () => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Self-service "rename my workspace" dialog. Renaming is owner-only server-side. */
export function EditTenantDialog({ open, onClose, tenant, loading, onUpdated }: EditTenantDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setName(tenant?.name ?? '');
  }, [open, tenant]);

  const update = useMutation({
    mutationFn: (newName: string) => resources.myTenant.update(newName),
    onSuccess: () => {
      onUpdated();
      onClose();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      await update.mutateAsync(name.trim());
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title="Edit workspace"
      submitLabel="Save"
      loading={update.isPending}
      onClose={() => {
        setError(null);
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Workspace name"
        required
        autoFocus
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={loading || update.isPending}
      />
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Created {tenant ? formatDate(tenant.createdAt) : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {(tenant?.owners.length ?? 0) > 1 ? 'Owners' : 'Owner'}:{' '}
          {tenant && tenant.owners.length > 0
            ? tenant.owners.map((o) => o.name ?? o.email).join(', ')
            : '—'}
        </Typography>
      </Box>
    </FormDialog>
  );
}
